import { useQuery, useZero } from "@rocicorp/zero/react";
import { classifyZeroResult } from "../lib/zeroResultError";

export function mutationErrorMessage(
  error: unknown,
  fallback = "Failed to connect connector"
): string {
  const decision = classifyZeroResult({ type: "error", error });
  if (decision.kind === "toast" && decision.message) return decision.message;
  return fallback;
}
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { getInitialConnectorValues } from "../../shared/connectors/getInitialConnectorValues";
import {
  isConnectorAvailableInScope,
  selectConnectors,
  type ConnectorAvailabilityScope,
} from "../../shared/connectors/searchConnectors";
import type { ConnectorDefinition } from "../../shared/connectors/types";
import { isTriviallyMatchingPattern } from "../../shared/connectors/urlPatternConversion";
import { urlToPattern } from "../../shared/connectors/urlToPattern";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { ClickButton } from "../components/ClickButton";
import { ConnectorLogo } from "../components/ConnectorLogo";
import { ConnectorRow } from "../components/ConnectorRow";
import { useRenderWithPlaceholders } from "../components/ConnectorModal/renderWithPlaceholders";
import { FooterAction } from "../components/FooterAction";
import { Icon } from "../components/Icon";
import { Keycap } from "../components/Keycap";
import { KeyboardHint } from "../components/KeyboardHint";
import { Text } from "../components/Text";
import { useCurrentUserGroups } from "../hooks/useCurrentUserGroups";
import { useCurrentUserGrants } from "../hooks/useCurrentUserGrants";
import { useDefaultConnectorGrants } from "../hooks/useDefaultConnectorGrants";
import { useGroupMemberships } from "../hooks/useGroupMemberships";
import {
  type SelectableConnector,
  useConnectorSelectOnSave,
} from "../hooks/useConnectorSelectOnSave";
import { useHasAccess } from "../hooks/useHasAccess";
import { useShiftHeld } from "../hooks/useShiftHeld";
import {
  connectorDirectory,
  recommendedCustomConnectors,
} from "../lib/connectorDirectory";
import {
  canCreateConnectorInChat,
  connectPromptState,
  creatableConnectorIdentifiers,
} from "../lib/connectPrompt";
import { useFormatRelativeTime } from "../hooks/useFormatRelativeTime";
import { isEditableTarget } from "../lib/isEditableTarget";

const PromptContainer = styled("div")`
  border-radius: 12px;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  padding: 4px;
`;

const PromptHeader = styled("div")`
  display: flex;
  padding: 10px 8px;
  box-sizing: content-box;
  align-items: center;
  color: var(--text-secondary);
`;

const PromptBody = styled("div")`
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
  @media (max-width: 768px) {
    max-height: 40dvh;
  }
`;

const Row = styled(ClickButton)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  padding-right: 28px;
  cursor: pointer;
  border-radius: 8px;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
  &[data-highlighted="true"] {
    background: var(--alpha4);
  }
`;

const Footer = styled("div")`
  display: flex;
  margin: 0 -4px;
  padding: 12px 12px 8px 12px;
  border-top: 0.5px solid var(--border-alpha);
  justify-content: space-between;
`;

const Instructions = styled("div")`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  gap: 4px;
`;

function urlConfigField(definition: ConnectorDefinition): string | undefined {
  for (const step of definition.steps) {
    for (const item of step.items) {
      if (
        (item.kind === "baseUrl" || item.kind === "fullUrl") &&
        item.target.kind === "configuration"
      ) {
        return item.target.field;
      }
    }
  }
  return undefined;
}

const UnavailableMessage = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 8px 8px;
`;

const UnavailableRow = styled("div")`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

type Pending = {
  toolCallId: string;
  step?: string;
  targetUrl?: string;
  search?: string;
  // When set, the prompt offers only that connector and never creation.
  connectorId?: string;
  appIdentifiers: string[];
};

type Props = {
  pending: Pending;
  openCreatedApp: (connectedAppId: string) => void;
  onDismiss: () => void;
  onTypeInstead: () => void;
  onNoApps?: () => void;
  onError?: (message: string) => void;
  onSelectApp: (app: SelectableConnector) => void | Promise<void>;
  spaceId?: string;
  othersPresent?: boolean;
};

export function ConnectorPrompt({
  pending,
  openCreatedApp,
  onDismiss,
  onTypeInstead,
  onNoApps,
  onError,
  onSelectApp,
  spaceId,
  othersPresent,
}: Props) {
  const z = useZero();
  const formatRelativeTime = useFormatRelativeTime();
  const renderWithPlaceholders = useRenderWithPlaceholders();
  const canCreateApp = useHasAccess("tenant:connected_app_create");
  const canShare = useHasAccess("tenant:connected_app_share");
  const [space, spaceResult] = useQuery(
    spaceId ? queries.navSpace({ spaceId }) : undefined
  );
  const { appIdentifiers = [], targetUrl, search, connectorId } = pending;
  const canCreateHere =
    !connectorId &&
    canCreateConnectorInChat({
      canCreateApp,
      canShare,
      spaceId,
      spaceMode: space?.mode,
    });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  const shiftHeld = useShiftHeld();

  const creatableIdentifiers = creatableConnectorIdentifiers(
    canCreateHere,
    appIdentifiers
  );

  const [allAppsRaw, allAppsDetails] = useQuery(
    queries.connectorsIndex({ access: "usable" })
  );
  const [spaceGrants, spaceGrantsDetails] = useQuery(
    spaceId ? queries.roleGrantsByScope({ scopeId: spaceId }) : undefined
  );
  const defaultConnectorGrants = useDefaultConnectorGrants(spaceId);
  const {
    currentUser,
    groupIds: currentUserGroupIds,
    loaded: currentUserGroupsLoaded,
  } = useCurrentUserGroups();
  const [, userGrantsResult] = useCurrentUserGrants();
  const {
    userGroupIds: allUserGroupMemberships,
    loaded: groupMembershipsLoaded,
  } = useGroupMemberships();
  const scope: ConnectorAvailabilityScope | null = spaceId
    ? space &&
      spaceResult.type === "complete" &&
      spaceGrants &&
      spaceGrantsDetails.type === "complete" &&
      groupMembershipsLoaded
      ? {
          kind: "space",
          mode: space.mode,
          grants: spaceGrants,
          userGroupMemberships: allUserGroupMemberships,
        }
      : null
    : currentUser && currentUserGroupsLoaded
      ? {
          kind: "personal_chat",
          userId: currentUser.id,
          userGroupIds: currentUserGroupIds,
        }
      : null;
  const available = scope
    ? allAppsRaw.filter((app) => isConnectorAvailableInScope(app, scope))
    : [];
  const matches = selectConnectors(
    available,
    connectorId ? {} : { targetUrl, search }
  ).apps;
  const existingApps = connectorId
    ? matches.filter((app) => app.id === connectorId)
    : matches;

  const handleSelectApp = async (app: SelectableConnector) => {
    await onSelectApp(app);
  };

  const trackCreatedApp = useConnectorSelectOnSave((connector) => {
    void handleSelectApp(connector);
  });

  const handleCreateNew = (identifier: string) => {
    const appDef = connectorDirectory.find(
      (app) => app.identifier === identifier
    );
    if (!appDef) return;

    const initialValues = getInitialConnectorValues(
      appDef.definition,
      renderWithPlaceholders
    );

    const configuration = { ...initialValues.configuration };
    let urlPattern = initialValues.urlPattern;
    let fallbackName = targetUrl ?? search ?? "";

    if (targetUrl) {
      let origin = targetUrl;
      try {
        const parsed = new URL(targetUrl);
        fallbackName = parsed.hostname;
        origin = parsed.origin;
      } catch {}

      const urlField = urlConfigField(appDef.definition);
      if (urlField && !configuration[urlField]) {
        configuration[urlField] = targetUrl;
      }

      if (!urlPattern) {
        const derived = appDef.definition.defaultUrlPattern(configuration);
        urlPattern =
          derived && !isTriviallyMatchingPattern(derived)
            ? derived
            : urlToPattern(origin);
      }
    }

    const newAppId = nanoid(12);
    if (!defaultConnectorGrants) return;

    const result = z.mutate(
      sharedMutators.createConnectedApp({
        connectedAppId: newAppId,
        name: initialValues.name || fallbackName,
        identifier,
        urlPattern,
        configuration,
        secrets: {},
        createOAuthConnection: initialValues.createOAuthConnection,
        grants: defaultConnectorGrants,
      })
    );
    trackCreatedApp(newAppId);
    openCreatedApp(newAppId);
    void result.server.then((serverResult) => {
      if (serverResult.type === "error") {
        onError?.(
          mutationErrorMessage(serverResult.error, "Failed to create connector")
        );
      }
    });
  };

  const showCustomConnectors =
    canCreateHere && creatableIdentifiers.length === 0;

  const handleConfirmSelected = useEffectEvent(() => {
    if (selectedIndex < existingApps.length) {
      const app = existingApps[selectedIndex];
      if (app) void handleSelectApp(app);
      return;
    }

    const createIndex = selectedIndex - existingApps.length;
    if (createIndex < creatableIdentifiers.length) {
      const identifier = creatableIdentifiers[createIndex];
      if (identifier) handleCreateNew(identifier);
      return;
    }

    if (showCustomConnectors) {
      const connector =
        recommendedCustomConnectors[createIndex - creatableIdentifiers.length];
      if (connector) handleCreateNew(connector.identifier);
    }
  });

  const handleOpenSelected = useEffectEvent(() => {
    if (selectedIndex < existingApps.length) {
      const app = existingApps[selectedIndex];
      if (app) openCreatedApp(app.id);
    }
  });

  const itemCount =
    existingApps.length +
    creatableIdentifiers.length +
    (showCustomConnectors ? recommendedCustomConnectors.length : 0);
  const state = connectPromptState({
    itemCount,
    singleViewerPath: !!onNoApps,
  });

  const appsQueryReady =
    allAppsDetails.type === "complete" &&
    userGrantsResult.type === "complete" &&
    scope !== null;
  const showPrompt = appsQueryReady && state !== "resolveEmpty";
  const firedNoApps = useRef(false);
  useEffect(() => {
    if (
      appsQueryReady &&
      state === "resolveEmpty" &&
      !firedNoApps.current &&
      onNoApps
    ) {
      firedNoApps.current = true;
      onNoApps();
    }
  }, [appsQueryReady, state, onNoApps]);

  useEffect(() => {
    if (!showPrompt) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditableTarget(e)) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        const nextIndex =
          e.key === "ArrowDown"
            ? Math.min(selectedIndex + 1, itemCount - 1)
            : Math.max(selectedIndex - 1, 0);
        setSelectedIndex(nextIndex);
        bodyRef.current
          ?.querySelectorAll("[data-highlighted]")
          [nextIndex]?.scrollIntoView({ block: "nearest" });
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) handleOpenSelected();
        else handleConfirmSelected();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) onTypeInstead();
        else onDismiss();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [itemCount, onDismiss, onTypeInstead, selectedIndex, showPrompt]);

  // resolveEmpty: onNoApps auto-resolves, so render nothing. unavailable: this
  // viewer can't act, so show why rather than resolving the prompt for every
  // collaborator.
  if (!showPrompt) return null;
  if (state === "unavailable") {
    return (
      <UnavailablePrompt
        target={targetUrl ?? search}
        message={
          connectorId
            ? spaceId
              ? "No longer available to this space."
              : "This connector isn’t available to you."
            : "No available connectors match this request, and you don’t have permission to create one."
        }
        othersPresent={!!othersPresent && !(spaceId && connectorId)}
        onDismiss={onDismiss}
        onTypeInstead={onTypeInstead}
      />
    );
  }

  return (
    <PromptContainer>
      <PromptBody ref={bodyRef}>
        <PromptHeader>
          <Text variant="text-small">
            Connect to {targetUrl ?? search ?? existingApps[0]?.name}
          </Text>
        </PromptHeader>
        {existingApps.map((app, index) => {
          return (
            <Row
              key={app.id}
              data-no-pill
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={(e) => {
                if (e.shiftKey) openCreatedApp(app.id);
                else void handleSelectApp(app);
              }}
              data-highlighted={index === selectedIndex}
            >
              <ConnectorRow
                app={app}
                size={16}
                name={app.name}
                secondary={
                  app.createdBy
                    ? `Added by ${app.createdBy.name || app.createdBy.email} ${formatRelativeTime(app.createdAt)}`
                    : `Added ${formatRelativeTime(app.createdAt)}`
                }
              />
              {index === selectedIndex && (
                <KeyboardHint
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <Keycap>
                    <Icon
                      name={shiftHeld ? "arrow-up-right" : "corner-down-left"}
                      size={12}
                    />
                  </Keycap>
                </KeyboardHint>
              )}
            </Row>
          );
        })}
        {creatableIdentifiers.map((identifier, offset) => {
          const index = existingApps.length + offset;
          const appDef = connectorDirectory.find(
            (app) => app.identifier === identifier
          );
          const hasExisting = existingApps.length > 0;
          return (
            <Row
              key={identifier}
              data-no-pill
              onClick={() => handleCreateNew(identifier)}
              onMouseEnter={() => setSelectedIndex(index)}
              data-highlighted={selectedIndex === index}
            >
              {hasExisting ? (
                <Icon name="plus" size={16} />
              ) : (
                <ConnectorLogo template={{ name: "", identifier }} size={16} />
              )}
              <Text variant="text-regular" color="var(--text-primary)">
                {hasExisting
                  ? `Create a new ${appDef?.definition.name ?? identifier} connection`
                  : `Connect with ${appDef?.definition.name ?? identifier}`}
              </Text>
              {selectedIndex === index && (
                <KeyboardHint
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <Keycap>
                    <Icon name="corner-down-left" size={12} />
                  </Keycap>
                </KeyboardHint>
              )}
            </Row>
          );
        })}
        {showCustomConnectors &&
          recommendedCustomConnectors.map((connector, offset) => {
            const index =
              existingApps.length + creatableIdentifiers.length + offset;
            return (
              <Row
                key={connector.identifier}
                data-no-pill
                onClick={() => handleCreateNew(connector.identifier)}
                onMouseEnter={() => setSelectedIndex(index)}
                data-highlighted={selectedIndex === index}
              >
                <ConnectorRow
                  template={{
                    name: connector.name,
                    identifier: connector.identifier,
                  }}
                  size={16}
                  name={
                    <>
                      {connector.name}{" "}
                      <Text variant="text-small" color="var(--text-secondary)">
                        {connector.tagLine}
                      </Text>
                    </>
                  }
                />
                {selectedIndex === index && (
                  <KeyboardHint
                    style={{
                      position: "absolute",
                      right: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  >
                    <Keycap>
                      <Icon name="corner-down-left" size={12} />
                    </Keycap>
                  </KeyboardHint>
                )}
              </Row>
            );
          })}
      </PromptBody>
      <Footer>
        <Instructions>
          <FooterAction onPress={onDismiss}>
            <Text variant="text-smaller-md">Skip</Text>
            <KeyboardHint>
              <Keycap style={{ padding: "2px 4px" }}>
                <Text variant="text-smaller-md">esc</Text>
              </Keycap>
            </KeyboardHint>
          </FooterAction>
          <Icon name="slash" size={12} color="var(--border-alpha)" />
          <FooterAction onPress={onTypeInstead}>
            <Text variant="text-smaller-md">Chat instead</Text>
            <KeyboardHint>
              <Keycap
                style={{ padding: "2px 4px", gap: 2, display: "inline-flex" }}
              >
                <Text variant="text-smaller-md">⇧</Text>
                <Text variant="text-smaller-md">esc</Text>
              </Keycap>
            </KeyboardHint>
          </FooterAction>
        </Instructions>
        <Instructions>
          {existingApps.length > 0 && (
            <>
              <KeyboardHint>
                <Text variant="text-smaller-md">Open </Text>
                <Keycap
                  style={{ padding: "2px 4px", gap: 2, display: "inline-flex" }}
                >
                  <Text variant="text-smaller-md">⇧</Text>
                  <Icon name="corner-down-left" size={12} />
                </Keycap>
              </KeyboardHint>
              <Icon name="slash" size={12} color="var(--border-alpha)" />
            </>
          )}
          <KeyboardHint>
            <Text variant="text-smaller-md">Select </Text>
            <Keycap>
              <Icon name="corner-down-left" size={12} />
            </Keycap>
          </KeyboardHint>
        </Instructions>
      </Footer>
    </PromptContainer>
  );
}

function UnavailablePrompt({
  target,
  message,
  othersPresent,
  onDismiss,
  onTypeInstead,
}: {
  target?: string;
  message: string;
  othersPresent: boolean;
  onDismiss: () => void;
  onTypeInstead: () => void;
}) {
  return (
    <PromptContainer>
      <PromptHeader>
        <Text variant="text-small">
          {target ? `Connect to ${target}` : "Connect a connector"}
        </Text>
      </PromptHeader>
      <UnavailableMessage>
        <UnavailableRow>
          <Icon name="lock" size={16} color="var(--text-secondary)" />
          <Text variant="text-regular" color="var(--text-primary)">
            {message}
          </Text>
        </UnavailableRow>
        {othersPresent && (
          <UnavailableRow>
            <Icon name="users-round" size={16} color="var(--text-secondary)" />
            <Text variant="text-small" color="var(--text-secondary)">
              A collaborator with access can connect it — skip only if no one
              will.
            </Text>
          </UnavailableRow>
        )}
      </UnavailableMessage>
      <Footer>
        <Instructions>
          <FooterAction onPress={onDismiss}>
            <Text variant="text-smaller-md">Skip</Text>
            <KeyboardHint>
              <Keycap style={{ padding: "2px 4px" }}>
                <Text variant="text-smaller-md">esc</Text>
              </Keycap>
            </KeyboardHint>
          </FooterAction>
          <Icon name="slash" size={12} color="var(--border-alpha)" />
          <FooterAction onPress={onTypeInstead}>
            <Text variant="text-smaller-md">Chat instead</Text>
            <KeyboardHint>
              <Keycap
                style={{ padding: "2px 4px", gap: 2, display: "inline-flex" }}
              >
                <Text variant="text-smaller-md">⇧</Text>
                <Text variant="text-smaller-md">esc</Text>
              </Keycap>
            </KeyboardHint>
          </FooterAction>
        </Instructions>
      </Footer>
    </PromptContainer>
  );
}
